<template>
  <div class="route-making-container" dir="rtl">
    <!-- ==================== PAGE HEADER ==================== -->
    <div class="rm-header-row mb-6">
      <div class="rm-header-titles">
        <span class="rm-badge-section">المسارات</span>
        <h1 class="rm-main-title">إدارة المسارات</h1>
        <p class="rm-subtitle">إدارة جميع مسارات النقل ومراجعة تفاصيلها ومساراتها</p>
      </div>
      <div class="rm-header-actions d-flex align-center gap-2">
        <v-btn
          depressed
          class="rm-btn-secondary"
          @click="exportRoutesCsv"
          :disabled="routes.length === 0"
        >
          <v-icon right size="18" class="ms-1">mdi-download</v-icon>
          تصدير CSV
        </v-btn>

        <v-btn
          depressed
          class="rm-btn-primary"
          @click="createRoute"
        >
          <v-icon right size="18" class="ms-1">mdi-plus</v-icon>
          إضافة مسار جديد
        </v-btn>
      </div>
    </div>

    <!-- ==================== SUMMARY CARDS ==================== -->
    <div class="rm-cards-grid mb-6">
      <!-- Card 1: جميع المسارات -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">جميع المسارات</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value">{{ routes.length }}</span>
            <v-skeleton-loader v-else type="text" width="60" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">إجمالي المسارات المسجلة</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-blue">
          <v-icon size="26" color="#082C69">mdi-road-variant</v-icon>
        </div>
      </div>

      <!-- Card 2: المسارات النشطة -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">المسارات النشطة</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value rm-val-active">{{ activeRoutesCount }}</span>
            <v-skeleton-loader v-else type="text" width="60" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">المسارات المفعلة في الخدمة</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-green">
          <v-icon size="26" color="#10B981">mdi-check-circle-outline</v-icon>
        </div>
      </div>

      <!-- Card 3: المسارات غير النشطة -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">المسارات غير النشطة</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value rm-val-suspended">{{ inactiveRoutesCount }}</span>
            <v-skeleton-loader v-else type="text" width="60" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">المسارات غير النشطة حالياً</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-amber">
          <v-icon size="26" color="#F59E0B">mdi-pause-circle-outline</v-icon>
        </div>
      </div>
    </div>

    <!-- ==================== FILTER & SEARCH BAR ==================== -->
    <div class="rm-filter-card mb-6">
      <div class="rm-filter-row">
        <!-- Search Input -->
        <div class="rm-search-box flex-grow-1">
          <v-text-field
            v-model="searchQuery"
            dense
            outlined
            hide-details
            clearable
            placeholder="ابحث عن مسار بالاسم أو الكود أو نقطة البداية أو النهاية..."
            class="rm-search-input"
            @click:clear="searchQuery = ''"
          >
            <template v-slot:prepend-inner>
              <v-icon size="20" color="#64748B" class="me-2">mdi-magnify</v-icon>
            </template>
          </v-text-field>
        </div>

        <!-- Status Filter -->
        <div class="rm-filter-select-wrap">
          <v-select
            v-model="statusFilter"
            :items="statusOptions"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details
            class="rm-custom-select"
            append-icon="mdi-chevron-down"
          ></v-select>
        </div>

        <!-- Region Filter -->
        <div class="rm-filter-select-wrap">
          <v-select
            v-model="regionFilter"
            :items="regionOptions"
            dense
            outlined
            hide-details
            class="rm-custom-select"
            append-icon="mdi-chevron-down"
          ></v-select>
        </div>

        <!-- Sort Filter -->
        <div class="rm-filter-select-wrap">
          <v-select
            v-model="sortBy"
            :items="sortOptions"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details
            class="rm-custom-select"
            append-icon="mdi-chevron-down"
          ></v-select>
        </div>

        <!-- Reset Button if filtered -->
        <div v-if="isFiltered" class="rm-reset-wrap">
          <v-btn
            text
            small
            color="primary"
            class="rm-reset-btn"
            @click="resetFilters"
          >
            <v-icon size="16" class="me-1">mdi-refresh</v-icon>
            إعادة تعيين
          </v-btn>
        </div>
      </div>
    </div>

    <!-- ==================== ROUTES TABLE CARD ==================== -->
    <div class="rm-table-card">
      <!-- Loading State -->
      <div v-if="isLoading" class="rm-loading-box py-12 text-center">
        <v-progress-circular
          indeterminate
          color="#082C69"
          size="42"
          width="3.5"
          class="mb-3"
        ></v-progress-circular>
        <div class="text-subtitle-2 font-weight-medium text--secondary">
          جاري تحميل المسارات...
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="paginatedRoutes.length === 0"
        class="rm-empty-state py-12 text-center"
      >
        <div class="rm-empty-icon-wrap mb-3">
          <v-icon size="56" color="#94A3B8">mdi-map-marker-path</v-icon>
        </div>
        <h3 class="rm-empty-title mb-1">لا توجد مسارات حالياً</h3>
        <p class="rm-empty-desc mb-5">
          {{ searchQuery ? 'لم يتم العثور على مسارات مطابقة لبحثك.' : 'لم يتم إضافة أي مسارات نقل حتى الآن.' }}
        </p>
        <v-btn
          v-if="!searchQuery"
          depressed
          class="rm-btn-primary"
          @click="createRoute"
        >
          <v-icon right size="18" class="ms-1">mdi-plus</v-icon>
          إضافة مسار جديد
        </v-btn>
        <v-btn
          v-else
          text
          color="primary"
          @click="resetFilters"
        >
          إلغاء البحث
        </v-btn>
      </div>

      <!-- Table View -->
      <div v-else class="rm-table-wrapper">
        <table class="rm-modern-table">
          <thead>
            <tr>
              <th class="rm-th-index text-center">#</th>
              <th class="rm-th-code">رقم المسار</th>
              <th class="rm-th-name">اسم المسار</th>
              <th class="rm-th-points">نقطة البداية ← النهاية</th>
              <th class="rm-th-stops text-center">المحطات</th>
              <th class="rm-th-distance text-center">المسافة</th>
              <th class="rm-th-fare text-center">سعر المسار</th>
              <th class="rm-th-kmfare text-center">سعر الكيلو</th>
              <th class="rm-th-pct rm-th-pct-min text-center">الحد الأدنى (%)</th>
              <th class="rm-th-pct rm-th-pct-weekly text-center">خصم أسبوعي (%)</th>
              <th class="rm-th-pct rm-th-pct-monthly text-center">خصم شهري (%)</th>
              <th class="rm-th-status text-center">الحالة</th>
              <th class="rm-th-actions text-center">الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in paginatedRoutes"
              :key="item.id"
              class="rm-table-row"
            >
              <!-- 1. # Index -->
              <td class="rm-td-index text-center">
                <span class="rm-row-index">{{ (currentPage - 1) * itemsPerPage + index + 1 }}</span>
              </td>

              <!-- 2. رقم المسار -->
              <td class="rm-td-code">
                <span class="rm-code-pill">
                  {{ item.route_code || item.code || ('R-' + String(item.id).padStart(4, '0')) }}
                </span>
              </td>

              <!-- 3. اسم المسار (Max 2 lines + Tooltip) -->
              <td class="rm-td-name">
                <v-tooltip bottom :disabled="!item.name || item.name.length < 22">
                  <template v-slot:activator="{ on, attrs }">
                    <div class="rm-name-cell" v-bind="attrs" v-on="on">
                      <div class="rm-route-icon-circle me-1">
                        <v-icon size="13" color="#082C69">mdi-road-variant</v-icon>
                      </div>
                      <span class="rm-route-name-text">{{ item.name }}</span>
                    </div>
                  </template>
                  <span>{{ item.name }}</span>
                </v-tooltip>
              </td>

              <!-- 4. نقطة البداية ← نقطة النهاية (Compact 2-line max + Tooltip) -->
              <td class="rm-td-points">
                <div v-if="getRouteDetail(item.id, 'loading')" class="rm-loading-shimmer">
                  <v-skeleton-loader type="text" width="130" class="my-1"></v-skeleton-loader>
                </div>
                <v-tooltip bottom v-else-if="getRoutePoints(item)">
                  <template v-slot:activator="{ on, attrs }">
                    <div class="rm-points-compact" v-bind="attrs" v-on="on">
                      <span class="rm-point-item rm-point-start">
                        <span class="rm-point-dot dot-start"></span>
                        <span class="rm-point-text">{{ getRoutePoints(item).start }}</span>
                      </span>
                      <span class="rm-point-arrow">←</span>
                      <span class="rm-point-item rm-point-end" v-if="getRoutePoints(item).end">
                        <span class="rm-point-dot dot-end"></span>
                        <span class="rm-point-text">{{ getRoutePoints(item).end }}</span>
                      </span>
                    </div>
                  </template>
                  <span>{{ getRoutePoints(item).start }} ← {{ getRoutePoints(item).end }}</span>
                </v-tooltip>
                <span v-else class="text--disabled">—</span>
              </td>

              <!-- 5. عدد المحطات -->
              <td class="rm-td-stops text-center">
                <span v-if="hasValue(item.stops_count)" class="rm-stops-badge">
                  {{ item.stops_count }} محطة
                </span>
                <span v-else class="text--disabled">0</span>
              </td>

              <!-- 6. المسافة (كم) -->
              <td class="rm-td-distance text-center">
                <div v-if="getRouteDetail(item.id, 'loading')" class="rm-loading-shimmer">
                  <v-skeleton-loader type="text" width="40" class="mx-auto my-1"></v-skeleton-loader>
                </div>
                <span v-else-if="getRouteDistance(item)" class="rm-dist-text">
                  {{ getRouteDistance(item) }} كم
                </span>
                <span v-else class="text--disabled">—</span>
              </td>

              <!-- 7. سعر المسار -->
              <td class="rm-td-fare text-center">
                <span v-if="hasValue(getRouteFullPrice(item))" class="rm-fare-text">
                  {{ formatNumber(getRouteFullPrice(item)) }} ج.م
                </span>
                <span v-else class="text--disabled">—</span>
              </td>

              <!-- 8. سعر الكيلو -->
              <td class="rm-td-kmfare text-center">
                <span v-if="hasValue(getRoutePricePerKm(item))" class="rm-kmfare-text">
                  {{ formatNumber(getRoutePricePerKm(item)) }} ج.م
                </span>
                <span v-else class="text--disabled">—</span>
              </td>

              <!-- 9. نسبة الحد الأدنى (%) -->
              <td class="rm-td-pct rm-td-pct-min text-center">
                <span v-if="hasValue(getRouteMinimumPercentage(item))" class="rm-pct-badge rm-pct-min">
                  {{ formatPercent(getRouteMinimumPercentage(item)) }}%
                </span>
                <span v-else class="text--disabled">—</span>
              </td>

              <!-- 10. خصم الأسبوعي (%) -->
              <td class="rm-td-pct rm-td-pct-weekly text-center">
                <span v-if="hasValue(getRouteWeeklyDiscount(item))" class="rm-pct-badge rm-pct-weekly">
                  {{ formatPercent(getRouteWeeklyDiscount(item)) }}%
                </span>
                <span v-else class="text--disabled">0%</span>
              </td>

              <!-- 11. خصم الشهري (%) -->
              <td class="rm-td-pct rm-td-pct-monthly text-center">
                <span v-if="hasValue(getRouteMonthlyDiscount(item))" class="rm-pct-badge rm-pct-monthly">
                  {{ formatPercent(getRouteMonthlyDiscount(item)) }}%
                </span>
                <span v-else class="text--disabled">0%</span>
              </td>

              <!-- 12. الحالة -->
              <td class="rm-td-status text-center">
                <span
                  :class="['rm-status-badge', isItemActive(item) ? 'rm-badge-active' : 'rm-badge-inactive']"
                >
                  <span class="rm-status-dot"></span>
                  {{ isItemActive(item) ? 'نشط' : 'غير نشط' }}
                </span>
              </td>

              <!-- 13. الإجراءات -->
              <td class="rm-td-actions text-center">
                <div class="rm-actions-group">
                  <!-- View Details -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <button
                        type="button"
                        class="rm-action-btn rm-btn-view"
                        v-bind="attrs"
                        v-on="on"
                        aria-label="عرض المسار"
                        @click="viewRoute(item)"
                      >
                        <v-icon size="17">mdi-eye-outline</v-icon>
                      </button>
                    </template>
                    <span>عرض المسار</span>
                  </v-tooltip>

                  <!-- Edit Route -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <button
                        type="button"
                        class="rm-action-btn rm-btn-edit"
                        v-bind="attrs"
                        v-on="on"
                        aria-label="تعديل المسار"
                        @click="editRoute(item)"
                      >
                        <v-icon size="17">mdi-pencil-outline</v-icon>
                      </button>
                    </template>
                    <span>تعديل المسار</span>
                  </v-tooltip>

                  <!-- Activate / Deactivate Route -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <button
                        type="button"
                        :class="['rm-action-btn', isItemActive(item) ? 'rm-btn-deactivate' : 'rm-btn-activate']"
                        v-bind="attrs"
                        v-on="on"
                        :aria-label="isItemActive(item) ? 'تعطيل المسار' : 'تفعيل المسار'"
                        @click="toggleRouteStatus(item)"
                      >
                        <v-icon size="17">
                          {{ isItemActive(item) ? 'mdi-pause-circle-outline' : 'mdi-check-circle-outline' }}
                        </v-icon>
                      </button>
                    </template>
                    <span>{{ isItemActive(item) ? 'تعطيل المسار' : 'تفعيل المسار' }}</span>
                  </v-tooltip>

                  <!-- Delete Route -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <button
                        type="button"
                        class="rm-action-btn rm-btn-delete"
                        v-bind="attrs"
                        v-on="on"
                        aria-label="حذف المسار"
                        @click="deleteRoute(item)"
                      >
                        <v-icon size="17">mdi-delete-outline</v-icon>
                      </button>
                    </template>
                    <span>حذف المسار</span>
                  </v-tooltip>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ==================== PAGINATION FOOTER ==================== -->
      <div v-if="filteredRoutes.length > 0" class="rm-pagination-bar">
        <div class="rm-pagination-info">
          <span>عرض </span>
          <strong>{{ paginationRange.start }} - {{ paginationRange.end }}</strong>
          <span> من أصل </span>
          <strong>{{ filteredRoutes.length }}</strong>
          <span> مسار</span>
        </div>

        <div class="rm-pagination-controls">
          <!-- Page Size Selector -->
          <div class="rm-page-size-wrap me-4">
            <span class="rm-page-size-label me-2">لكل صفحة:</span>
            <v-select
              v-model="itemsPerPage"
              :items="[10, 25, 50, 100]"
              dense
              outlined
              hide-details
              class="rm-page-size-select"
              append-icon="mdi-chevron-down"
              @change="currentPage = 1"
            ></v-select>
          </div>

          <!-- Page Navigation Buttons -->
          <div class="rm-nav-buttons">
            <!-- Next Button (in RTL, previous goes to right/higher, next goes to left) -->
            <button
              type="button"
              class="rm-nav-btn"
              :disabled="currentPage === 1"
              @click="currentPage--"
              aria-label="الصفحة السابقة"
            >
              <v-icon size="18">mdi-chevron-right</v-icon>
            </button>

            <span
              v-for="p in visiblePages"
              :key="p"
              :class="['rm-page-num', { 'active-page': p === currentPage, 'dots-page': p === '...' }]"
              @click="typeof p === 'number' && (currentPage = p)"
            >
              {{ p }}
            </span>

            <button
              type="button"
              class="rm-nav-btn"
              :disabled="currentPage === totalPages"
              @click="currentPage++"
              aria-label="الصفحة التالية"
            >
              <v-icon size="18">mdi-chevron-left</v-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import auth from "@/services/AuthService";
import { exportToCsv } from "@/utils/csvExport";

export default {
  name: "RouteMakingRoutes",
  data() {
    return {
      routes: [],
      isLoading: false,
      searchQuery: "",
      statusFilter: "all",
      regionFilter: "all",
      sortBy: "newest",
      currentPage: 1,
      itemsPerPage: 10,
      statusOptions: [
        { text: "جميع الحالات", value: "all" },
        { text: "نشط", value: "active" },
        { text: "غير نشط", value: "inactive" },
      ],
      regionOptions: [
        { text: "جميع المناطق", value: "all" },
      ],
      sortOptions: [
        { text: "الأحدث أولاً", value: "newest" },
        { text: "الأقدم أولاً", value: "oldest" },
        { text: "الاسم (أ - ي)", value: "name_asc" },
        { text: "الاسم (ي - أ)", value: "name_desc" },
        { text: "عدد المحطات (الأكثر)", value: "stops_desc" },
        { text: "عدد المحطات (الأقل)", value: "stops_asc" },
      ],
      routeDetailsCache: {},
    };
  },
  computed: {
    hasRealStatus() {
      return true;
    },
    activeRoutesCount() {
      return this.routes.filter((r) => this.isItemActive(r)).length;
    },
    inactiveRoutesCount() {
      return this.routes.filter((r) => !this.isItemActive(r)).length;
    },
    isFiltered() {
      return (
        (this.searchQuery && this.searchQuery.trim().length > 0) ||
        this.statusFilter !== "all" ||
        this.regionFilter !== "all" ||
        this.sortBy !== "newest"
      );
    },
    filteredRoutes() {
      let result = [...this.routes];

      // 1. Search filter
      if (this.searchQuery && this.searchQuery.trim().length > 0) {
        const q = this.searchQuery.trim().toLowerCase();
        result = result.filter((item) => {
          const nameMatch = (item.name || "").toLowerCase().includes(q);
          const codeMatch = (item.route_code || item.code || item.route_number || "")
            .toString()
            .toLowerCase()
            .includes(q);
          const originMatch = (item.start_stop_name || item.origin || item.start_stop || "")
            .toLowerCase()
            .includes(q);
          const destMatch = (item.end_stop_name || item.destination || item.end_stop || "")
            .toLowerCase()
            .includes(q);
          const idMatch = (item.id || "").toString() === q ||
                          ("r-" + item.id).toLowerCase() === q ||
                          ("r-" + String(item.id).padStart(4, "0")).toLowerCase() === q;
          return nameMatch || codeMatch || originMatch || destMatch || idMatch;
        });
      }

      // 2. Status filter
      if (this.statusFilter !== "all") {
        if (this.statusFilter === "active") {
          result = result.filter((r) => this.isItemActive(r));
        } else if (this.statusFilter === "inactive" || this.statusFilter === "suspended") {
          result = result.filter((r) => !this.isItemActive(r));
        }
      }

      // 3. Sorting
      result.sort((a, b) => {
        if (this.sortBy === "newest") {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : a.id || 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : b.id || 0;
          return dateB - dateA;
        }
        if (this.sortBy === "oldest") {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : a.id || 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : b.id || 0;
          return dateA - dateB;
        }
        if (this.sortBy === "name_asc") {
          return (a.name || "").localeCompare(b.name || "", "ar");
        }
        if (this.sortBy === "name_desc") {
          return (b.name || "").localeCompare(a.name || "", "ar");
        }
        if (this.sortBy === "stops_desc") {
          return (b.stops_count || 0) - (a.stops_count || 0);
        }
        if (this.sortBy === "stops_asc") {
          return (a.stops_count || 0) - (b.stops_count || 0);
        }
        return 0;
      });

      return result;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredRoutes.length / this.itemsPerPage));
    },
    paginatedRoutes() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredRoutes.slice(start, start + this.itemsPerPage);
    },
    paginationRange() {
      if (this.filteredRoutes.length === 0) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.itemsPerPage + 1;
      const end = Math.min(
        this.currentPage * this.itemsPerPage,
        this.filteredRoutes.length
      );
      return { start, end };
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      if (current <= 4) {
        return [1, 2, 3, 4, 5, "...", total];
      }
      if (current >= total - 3) {
        return [1, "...", total - 4, total - 3, total - 2, total - 1, total];
      }
      return [1, "...", current - 1, current, current + 1, "...", total];
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    statusFilter() {
      this.currentPage = 1;
    },
    regionFilter() {
      this.currentPage = 1;
    },
    sortBy() {
      this.currentPage = 1;
    },
    paginatedRoutes: {
      handler() {
        this.enrichCurrentPageRoutes();
      },
      immediate: true,
    },
  },
  mounted() {
    this.loadRoutes();
  },
  methods: {
    exportRoutesCsv() {
      const routesToExport = this.filteredRoutes && this.filteredRoutes.length > 0 ? this.filteredRoutes : this.routes;
      exportToCsv({
        filename: `مسارات-${new Date().toISOString().slice(0, 10)}.csv`,
        metadata: [
          { label: "إجمالي المسارات المسجلة", value: this.routes.length },
          { label: "المسارات المعروضة", value: routesToExport.length },
          { label: "تاريخ التصدير", value: new Date().toLocaleString("ar-EG") },
        ],
        headers: [
          "رقم المسار",
          "اسم المسار",
          "المحطة الأولى",
          "المحطة الأخيرة",
          "عدد المحطات",
          "المسافة (كم)",
          "سعر المسار",
          "سعر الكيلو",
          "نسبة الحد الأدنى (%)",
          "خصم الأسبوعي (%)",
          "خصم الشهري (%)",
          "الحالة",
        ],
        rows: routesToExport.map((r) => [
          r.route_code || `R-${String(r.id).padStart(4, "0")}`,
          r.name || "",
          r.start_stop_name || (r.stops && r.stops.length > 0 ? r.stops[0].name : "—"),
          r.end_stop_name || (r.stops && r.stops.length > 1 ? r.stops[r.stops.length - 1].name : "—"),
          r.stops_count || (r.stops ? r.stops.length : 0),
          this.getRouteDistance(r) || "—",
          this.getRouteFullPrice(r) || "—",
          this.getRoutePricePerKm(r) || "—",
          this.hasValue(this.getRouteMinimumPercentage(r)) ? `${this.formatPercent(this.getRouteMinimumPercentage(r))}%` : "—",
          this.hasValue(this.getRouteWeeklyDiscount(r)) ? `${this.formatPercent(this.getRouteWeeklyDiscount(r))}%` : "0%",
          this.hasValue(this.getRouteMonthlyDiscount(r)) ? `${this.formatPercent(this.getRouteMonthlyDiscount(r))}%` : "0%",
          this.isItemActive(r) ? "نشط" : "غير نشط",
        ]),
      });
    },
    hasValue(val) {
      return val !== null && val !== undefined && val !== "" && val !== "null";
    },
    formatNumber(val) {
      if (!this.hasValue(val)) return "—";
      const num = parseFloat(val);
      if (isNaN(num)) return val;
      return Number.isInteger(num) ? num.toString() : num.toFixed(2);
    },
    formatPercent(val) {
      if (!this.hasValue(val)) return "0";
      const num = parseFloat(val);
      if (isNaN(num)) return "0";
      return Number.isInteger(num) ? num.toString() : num.toFixed(1).replace(/\.0$/, "");
    },
    isItemActive(item) {
      if (!item) return false;
      if (item.is_active !== undefined && item.is_active !== null) {
        return item.is_active === true || item.is_active === 1 || item.is_active === "1";
      }
      if (item.status !== undefined && item.status !== null) {
        return (
          item.status === 1 ||
          item.status === "1" ||
          item.status === true ||
          item.status === "active" ||
          item.status === "نشط"
        );
      }
      return true;
    },
    getRouteDetail(routeId, field) {
      const detail = this.routeDetailsCache[routeId];
      if (!detail) return null;
      return detail[field];
    },
    getRoutePoints(item) {
      if (!item) return null;
      if (item.start_stop_name || item.end_stop_name) {
        return {
          start: item.start_stop_name || "",
          end: item.end_stop_name || "",
        };
      }
      if (Array.isArray(item.stops) && item.stops.length > 0) {
        return {
          start: item.stops[0].name || item.stops[0].address || "",
          end: item.stops.length > 1 ? (item.stops[item.stops.length - 1].name || item.stops[item.stops.length - 1].address || "") : "",
        };
      }
      const cached = this.routeDetailsCache[item.id];
      if (cached && (cached.startStop || cached.endStop)) {
        return {
          start: cached.startStop || "",
          end: cached.endStop || "",
        };
      }
      if (item.origin && item.destination) {
        return { start: item.origin, end: item.destination };
      }
      if (item.start_stop && item.end_stop) {
        return { start: item.start_stop, end: item.end_stop };
      }
      return null;
    },
    getRouteDistance(item) {
      if (!item) return null;
      if (this.hasValue(item.distance) && parseFloat(item.distance) > 0) {
        return parseFloat(item.distance).toFixed(2);
      }
      const cached = this.routeDetailsCache[item.id];
      if (cached && cached.distanceKm) {
        return cached.distanceKm;
      }
      return null;
    },
    getRouteFullPrice(item) {
      if (!item) return null;
      if (this.hasValue(item.full_trip_price) && parseFloat(item.full_trip_price) > 0) {
        return item.full_trip_price;
      }
      const cached = this.routeDetailsCache[item.id];
      if (cached && this.hasValue(cached.fullTripPrice)) {
        return cached.fullTripPrice;
      }
      if (this.hasValue(item.price) && parseFloat(item.price) > 0) {
        return item.price;
      }
      if (this.hasValue(item.fare) && parseFloat(item.fare) > 0) {
        return item.fare;
      }
      return null;
    },
    getRouteMinimumPercentage(item) {
      if (!item) return null;
      if (this.hasValue(item.minimum_percentage)) {
        return item.minimum_percentage;
      }
      const cached = this.routeDetailsCache[item.id];
      if (cached && this.hasValue(cached.minimumPercentage)) {
        return cached.minimumPercentage;
      }
      return null;
    },
    getRouteWeeklyDiscount(item) {
      if (!item) return null;
      if (this.hasValue(item.weekly_discount_percentage)) {
        return item.weekly_discount_percentage;
      }
      const cached = this.routeDetailsCache[item.id];
      if (cached && this.hasValue(cached.weeklyDiscount)) {
        return cached.weeklyDiscount;
      }
      return 0;
    },
    getRouteMonthlyDiscount(item) {
      if (!item) return null;
      if (this.hasValue(item.monthly_discount_percentage)) {
        return item.monthly_discount_percentage;
      }
      const cached = this.routeDetailsCache[item.id];
      if (cached && this.hasValue(cached.monthlyDiscount)) {
        return cached.monthlyDiscount;
      }
      return 0;
    },
    getRoutePricePerKm(item) {
      if (!item) return null;
      if (this.hasValue(item.price_per_km) && parseFloat(item.price_per_km) > 0) {
        return parseFloat(item.price_per_km).toFixed(2);
      }
      const cached = this.routeDetailsCache[item.id];
      const fullPrice = this.getRouteFullPrice(item);
      const minPct = this.getRouteMinimumPercentage(item);
      const dist = this.getRouteDistance(item);

      if (this.hasValue(fullPrice) && this.hasValue(minPct) && this.hasValue(dist)) {
        const f = parseFloat(fullPrice);
        const p = parseFloat(minPct) / 100.0;
        const d = parseFloat(dist);
        if (f > 0 && p > 0 && d > 0) {
          const minPrice = f * p;
          const rem = f - minPrice;
          return (rem / d).toFixed(2);
        }
      }

      if (cached && this.hasValue(cached.pricePerKm)) {
        return parseFloat(cached.pricePerKm).toFixed(2);
      }
      return null;
    },
    enrichCurrentPageRoutes() {
      if (!this.paginatedRoutes || this.paginatedRoutes.length === 0) return;
      this.paginatedRoutes.forEach((item) => {
        if (!item || !item.id) return;
        // Skip enrichment if /routes/all already provided stops and distance
        if (item.start_stop_name !== undefined && item.distance !== undefined) return;
        if (this.routeDetailsCache[item.id]) return;

        this.$set(this.routeDetailsCache, item.id, {
          loading: true,
          startStop: null,
          endStop: null,
          distanceKm: null,
          fullTripPrice: null,
          minimumPercentage: null,
          weeklyDiscount: null,
          monthlyDiscount: null,
          pricePerKm: null,
        });

        axios
          .get(`/routes/${item.id}`)
          .then((res) => {
            const data = res.data;
            let startStop = null;
            let endStop = null;
            let distanceKm = null;
            let fullTripPrice = data && data.full_trip_price ? data.full_trip_price : null;
            let minimumPercentage = data && data.minimum_percentage ? data.minimum_percentage : null;
            let weeklyDiscount = data && data.weekly_discount_percentage !== undefined ? data.weekly_discount_percentage : null;
            let monthlyDiscount = data && data.monthly_discount_percentage !== undefined ? data.monthly_discount_percentage : null;
            let pricePerKm = data && data.price_per_km ? data.price_per_km : null;

            if (data && Array.isArray(data.stops) && data.stops.length > 0) {
              const stops = [...data.stops];
              stops.sort((a, b) => {
                const orderA =
                  a.pivot && a.pivot.order !== undefined
                    ? a.pivot.order
                    : a.order !== undefined
                    ? a.order
                    : 0;
                const orderB =
                  b.pivot && b.pivot.order !== undefined
                    ? b.pivot.order
                    : b.order !== undefined
                    ? b.order
                    : 0;
                return orderA - orderB;
              });

              if (stops.length >= 2) {
                startStop = stops[0].name || stops[0].address || null;
                endStop = stops[stops.length - 1].name || stops[stops.length - 1].address || null;
              } else if (stops.length === 1) {
                startStop = stops[0].name || stops[0].address || null;
              }
            }

            if (data && data.distance && parseFloat(data.distance) > 0) {
              distanceKm = parseFloat(data.distance).toFixed(2);
            }

            this.$set(this.routeDetailsCache, item.id, {
              loading: false,
              startStop,
              endStop,
              distanceKm,
              fullTripPrice,
              minimumPercentage,
              weeklyDiscount,
              monthlyDiscount,
              pricePerKm,
            });
          })
          .catch((err) => {
            console.error(`Failed to enrich route ${item.id}:`, err);
            this.$set(this.routeDetailsCache, item.id, {
              loading: false,
              startStop: null,
              endStop: null,
              distanceKm: null,
              fullTripPrice: null,
              minimumPercentage: null,
              weeklyDiscount: null,
              monthlyDiscount: null,
              pricePerKm: null,
            });
          });
      });
    },
    resetFilters() {
      this.searchQuery = "";
      this.statusFilter = "all";
      this.regionFilter = "all";
      this.sortBy = "newest";
      this.currentPage = 1;
    },
    loadRoutes() {
      this.isLoading = true;
      this.routes = [];
      axios
        .get(`/routes/all`)
        .then((response) => {
          this.routes = Array.isArray(response.data) ? response.data : [];
        })
        .catch((error) => {
          this.$notify({
            title: this.$t("Error") || "خطأ",
            text: this.$t("error_retrieving_routes") || "فشل تحميل قائمة المسارات",
            type: "error",
          });
          console.error("Failed to retrieve routes:", error);
          if (error && error.response && error.response.data) {
            auth.checkError(error.response.data.message, this.$router, this.$swal);
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    createRoute() {
      this.$router.push({ name: "create-route-direct" });
    },
    viewRoute(route) {
      this.$router.push({
        name: "view-route",
        params: { route_id: route.id },
      });
    },
    editRoute(route) {
      this.$swal
        .fire({
          title: this.$t("enter_route_name") || "أدخل اسم المسار",
          input: "text",
          inputValue: route.name,
          showCancelButton: true,
          cancelButtonText: this.$t("cancel") || "إلغاء",
          confirmButtonText: this.$t("save") || "حفظ",
          confirmButtonColor: "#082C69",
          customClass: {
            container: "swal-rtl",
          },
        })
        .then((result) => {
          if (result.isConfirmed) {
            const value = (result.value || "").trim();
            this.$router.push({
              name: "edit-route",
              params: {
                route_id: route.id,
                new_route_name: value ? value : "Untitled",
              },
            });
          }
        });
    },
    toggleRouteStatus(route) {
      const currentlyActive = this.isItemActive(route);
      const titleText = currentlyActive ? "تعطيل المسار" : "تفعيل المسار";
      const bodyText = currentlyActive
        ? "هل تريد تعطيل هذا المسار؟ لن يظهر للعملاء أو في إنشاء الرحلات والمجموعات الجديدة حتى يتم تفعيله مرة أخرى."
        : "هل تريد تفعيل هذا المسار؟";
      const confirmText = currentlyActive ? "نعم، تعطيل المسار" : "نعم، تفعيل المسار";
      const confirmColor = currentlyActive ? "#D97706" : "#10B981";

      this.$swal
        .fire({
          title: titleText,
          text: bodyText,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: confirmText,
          cancelButtonText: "إلغاء",
          confirmButtonColor: confirmColor,
          cancelButtonColor: "#64748B",
          customClass: {
            container: "swal-rtl",
          },
        })
        .then((result) => {
          if (result.isConfirmed) {
            axios
              .post(`/routes/${route.id}/toggle-status`, {
                is_active: !currentlyActive,
              })
              .then((res) => {
                const newActive = res.data && res.data.is_active !== undefined ? res.data.is_active : !currentlyActive;
                this.$set(route, "is_active", newActive);
                this.$set(route, "status_label", newActive ? "نشط" : "غير نشط");
                this.$notify({
                  title: "نجاح",
                  text: (res.data && res.data.message) || (newActive ? "تم تفعيل المسار بنجاح" : "تم تعطيل المسار بنجاح"),
                  type: "success",
                });
              })
              .catch((err) => {
                const msg =
                  (err.response && err.response.data && err.response.data.message) ||
                  "تعذر تغيير حالة المسار.";
                this.$swal.fire("تنبيه", msg, "warning");
              });
          }
        });
    },
    deleteRoute(route) {
      this.$swal
        .fire({
          title: this.$t("delete_route") || "حذف المسار",
          text:
            this.$t("confirm_delete_route", { name: route.name }) ||
            `هل أنت متأكد من رغبتك في حذف المسار "${route.name}"؟ سيتم أيضاً إزالة هذا المسار من أي مجموعات رحلات مرتبطة.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: this.$t("yes_delete") || "نعم، حذف",
          cancelButtonText: this.$t("cancel") || "إلغاء",
          confirmButtonColor: "#DC2626",
          cancelButtonColor: "#64748B",
          customClass: {
            container: "swal-rtl",
          },
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.deleteRouteServer(route.id);
          }
        });
    },
    deleteRouteServer(routeId) {
      axios
        .delete(`/routes/${routeId}`)
        .then(() => {
          const index = this.routes.findIndex((r) => r.id === routeId);
          if (index !== -1) {
            this.routes.splice(index, 1);
          }
          this.$notify({
            title: this.$t("Success") || "نجاح",
            text: this.$t("route_deleted") || "تم حذف المسار وتحديث مجموعات الرحلات المرتبطة بنجاح",
            type: "success",
          });
        })
        .catch((error) => {
          this.$notify({
            title: this.$t("Error") || "خطأ",
            text: this.$t("error_deleting_routes") || "فشل حذف المسار",
            type: "error",
          });
          if (error && error.response && error.response.data) {
            this.$swal.fire("خطأ", error.response.data.message, "error");
          }
        });
    },
  },
};
</script>

<style lang="scss" scoped>
/* ==========================================================================
   Route Making — Modern Premium B2B Dashboard Styles
   Identity: BeepGo Navy (#082C69), Gray surface (#F8FAFD), Cairo typography
   ========================================================================== */

.route-making-container {
  width: 100%;
  font-family: 'Cairo', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
  color: #1E293B;
  box-sizing: border-box;

  *, *::before, *::after {
    box-sizing: border-box;
  }
}

/* ==================== PAGE HEADER ==================== */
.rm-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;

  .rm-header-titles {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    .rm-badge-section {
      background: #EEF2F9;
      color: #082C69;
      font-size: 12px;
      font-weight: 700;
      border-radius: 6px;
      padding: 2px 10px;
      margin-bottom: 6px;
      letter-spacing: 0.3px;
    }

    .rm-main-title {
      font-size: 26px;
      font-weight: 800;
      color: #082C69;
      margin: 0 0 4px 0;
      line-height: 1.3;
    }

    .rm-subtitle {
      font-size: 14px;
      color: #64748B;
      margin: 0;
      font-weight: 500;
    }
  }

  .rm-header-actions {
    display: flex;
    align-items: center;

    .rm-btn-primary {
      background: #082C69 !important;
      color: #FFFFFF !important;
      font-weight: 700 !important;
      font-size: 14px !important;
      height: 44px !important;
      border-radius: 10px !important;
      padding: 0 20px !important;
      text-transform: none !important;
      box-shadow: 0 4px 14px rgba(8, 44, 105, 0.25) !important;
      transition: all 0.2s ease !important;

      &:hover {
        background: #061B40 !important;
        box-shadow: 0 6px 18px rgba(8, 44, 105, 0.35) !important;
        transform: translateY(-1px);
      }
    }
  }
}

/* ==================== SUMMARY CARDS ==================== */
.rm-cards-grid {
  display: grid !important;
  grid-template-columns: repeat(3, 1fr) !important;
  gap: 20px !important;
  margin-bottom: 24px !important;
  width: 100% !important;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr) !important;
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr !important;
  }
}

.rm-stat-card {
  background: #FFFFFF;
  border-radius: 16px;
  padding: 20px 24px;
  border: 1px solid #E2E8F0;
  box-shadow: 0 2px 10px rgba(8, 44, 105, 0.03);
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  min-height: 104px;

  &:hover {
    box-shadow: 0 6px 20px rgba(8, 44, 105, 0.06);
    border-color: #CBD5E1;
    transform: translateY(-2px);
  }

  .rm-stat-content {
    display: flex;
    flex-direction: column;

    .rm-stat-label {
      font-size: 13.5px;
      font-weight: 600;
      color: #64748B;
      margin-bottom: 4px;
    }

    .rm-stat-val-wrapper {
      min-height: 38px;
      display: flex;
      align-items: center;

      .rm-stat-value {
        font-size: 28px;
        font-weight: 800;
        color: #082C69;
        line-height: 1.2;

        &.rm-val-active {
          color: #10B981;
        }

        &.rm-val-suspended {
          color: #F59E0B;
        }
      }

      .rm-stat-empty {
        display: inline-block;
        min-height: 28px;
        min-width: 30px;
      }
    }

    .rm-stat-caption {
      font-size: 11.5px;
      color: #94A3B8;
      font-weight: 500;
      margin-top: 2px;
    }
  }

  .rm-stat-icon-box {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &.rm-icon-blue {
      background: rgba(8, 44, 105, 0.07);
    }

    &.rm-icon-green {
      background: rgba(16, 185, 129, 0.1);
    }

    &.rm-icon-amber {
      background: rgba(245, 158, 11, 0.1);
    }
  }
}

/* ==================== FILTER BAR ==================== */
.rm-filter-card {
  background: #FFFFFF;
  border-radius: 14px;
  padding: 14px 18px;
  border: 1px solid #E2E8F0;
  box-shadow: 0 2px 8px rgba(8, 44, 105, 0.02);

  .rm-filter-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;

    .rm-search-box {
      min-width: 280px;

      ::v-deep .v-input__slot {
        background: #F8FAFC !important;
        border-radius: 10px !important;
        min-height: 42px !important;
        font-size: 13.5px !important;

        fieldset {
          border-color: #E2E8F0 !important;
        }
      }

      ::v-deep .v-input--is-focused .v-input__slot fieldset {
        border-color: #082C69 !important;
      }
    }

    .rm-filter-select-wrap {
      min-width: 150px;
      max-width: 190px;

      ::v-deep .v-input__slot {
        background: #F8FAFC !important;
        border-radius: 10px !important;
        min-height: 42px !important;
        font-size: 13px !important;
        font-weight: 600 !important;
        color: #334155 !important;

        fieldset {
          border-color: #E2E8F0 !important;
        }
      }

      ::v-deep .v-select__selection {
        font-size: 13px !important;
        font-weight: 600 !important;
        color: #334155 !important;
      }
    }

    .rm-reset-wrap {
      .rm-reset-btn {
        font-size: 13px !important;
        font-weight: 700 !important;
        border-radius: 8px !important;
      }
    }
  }
}

/* ==================== TABLE CARD ==================== */
.rm-table-card {
  background: #FFFFFF;
  border-radius: 14px;
  border: 1px solid #E2E8F0;
  box-shadow: 0 2px 10px rgba(8, 44, 105, 0.03);
  overflow: hidden;
  width: 100%;

  .rm-table-wrapper {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: #CBD5E1 transparent;

    &::-webkit-scrollbar {
      height: 6px;
    }
    &::-webkit-scrollbar-track {
      background: #F8FAFC;
    }
    &::-webkit-scrollbar-thumb {
      background: #CBD5E1;
      border-radius: 4px;
    }
  }

  .rm-modern-table {
    width: 100%;
    min-width: 1140px;
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 13px;
    text-align: right;

    thead tr {
      background: #F8FAFC;
      border-bottom: 1px solid #E2E8F0;

      th {
        padding: 10px 8px;
        font-size: 11.5px;
        font-weight: 700;
        color: #475569;
        white-space: nowrap;
        border-bottom: 1px solid #E2E8F0;
        letter-spacing: 0.1px;
        vertical-align: middle;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    tbody tr {
      transition: background 0.15s ease;
      border-bottom: 1px solid #F1F5F9;
      height: 60px;

      &:hover {
        background: #F8FAFD;
      }

      &:last-child td {
        border-bottom: none;
      }

      td {
        padding: 9px 8px;
        vertical-align: middle;
        border-bottom: 1px solid #F1F5F9;
        color: #1E293B;
        overflow: hidden;
      }
    }

    /* Column Width Allocation (Total ~1140-1180px, perfectly fitting 1440 & 1920) */
    .rm-th-index, .rm-td-index {
      width: 42px;
      min-width: 42px;
      max-width: 44px;
      padding-left: 4px;
      padding-right: 4px;

      .rm-row-index {
        font-size: 11.5px;
        font-weight: 600;
        color: #94A3B8;
        font-variant-numeric: tabular-nums;
      }
    }

    .rm-th-code, .rm-td-code {
      width: 84px;
      min-width: 80px;
      max-width: 88px;

      .rm-code-pill {
        display: inline-block;
        padding: 2px 7px;
        background: #F1F5F9;
        border: 1px solid #E2E8F0;
        border-radius: 5px;
        font-size: 11.5px;
        font-weight: 700;
        color: #082C69;
        letter-spacing: 0.2px;
        white-space: nowrap;
      }
    }

    .rm-th-name, .rm-td-name {
      width: 165px;
      min-width: 145px;

      .rm-name-cell {
        display: flex;
        align-items: center;
        max-width: 100%;

        .rm-route-icon-circle {
          width: 22px;
          height: 22px;
          border-radius: 6px;
          background: rgba(8, 44, 105, 0.06);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .rm-route-name-text {
          font-weight: 700;
          color: #082C69;
          font-size: 13px;
          line-height: 1.35;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          word-break: break-word;
          max-height: 2.7em;
        }
      }
    }

    .rm-loading-shimmer {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      vertical-align: middle;

      ::v-deep .v-skeleton-loader__text {
        margin: 0 !important;
        height: 14px !important;
        border-radius: 4px !important;
      }
    }

    .rm-th-points, .rm-td-points {
      width: 235px;
      min-width: 210px;

      .rm-points-compact {
        display: flex;
        align-items: center;
        gap: 4px;
        background: #F8FAFC;
        border: 1px solid #E2E8F0;
        border-radius: 6px;
        padding: 4px 7px;
        font-size: 12px;
        font-weight: 600;
        color: #334155;
        line-height: 1.3;
        max-width: 100%;
        cursor: default;

        .rm-point-item {
          display: inline-flex;
          align-items: center;
          max-width: 45%;
          overflow: hidden;

          .rm-point-text {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        .rm-point-arrow {
          color: #94A3B8;
          font-size: 11px;
          flex-shrink: 0;
          font-weight: 700;
        }

        .rm-point-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          display: inline-block;
          flex-shrink: 0;
          margin-inline-end: 4px;

          &.dot-start {
            background: #10B981;
          }
          &.dot-end {
            background: #EF4444;
          }
        }
      }
    }

    .rm-th-stops, .rm-td-stops {
      width: 78px;
      min-width: 72px;
      max-width: 82px;

      .rm-stops-badge {
        display: inline-block;
        padding: 3px 8px;
        background: rgba(8, 44, 105, 0.06);
        color: #082C69;
        border-radius: 6px;
        font-size: 11.5px;
        font-weight: 700;
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }
    }

    .rm-th-distance, .rm-td-distance {
      width: 78px;
      min-width: 72px;
      max-width: 82px;

      .rm-dist-text {
        font-size: 12px;
        font-weight: 700;
        color: #334155;
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }
    }

    .rm-th-fare, .rm-td-fare {
      width: 82px;
      min-width: 76px;
      max-width: 88px;

      .rm-fare-text {
        font-size: 12px;
        font-weight: 700;
        color: #082C69;
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }
    }

    .rm-th-kmfare, .rm-td-kmfare {
      width: 78px;
      min-width: 72px;
      max-width: 82px;

      .rm-kmfare-text {
        font-size: 12px;
        font-weight: 700;
        color: #334155;
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }
    }

    .rm-th-pct, .rm-td-pct {
      width: 80px;
      min-width: 75px;
      max-width: 85px;

      .rm-pct-badge {
        display: inline-block;
        padding: 2px 7px;
        border-radius: 6px;
        font-size: 11.5px;
        font-weight: 700;
        white-space: nowrap;
        font-variant-numeric: tabular-nums;

        &.rm-pct-min {
          background: #F1F5F9;
          color: #334155;
          border: 1px solid #E2E8F0;
        }

        &.rm-pct-weekly {
          background: #EFF6FF;
          color: #1D4ED8;
          border: 1px solid #BFDBFE;
        }

        &.rm-pct-monthly {
          background: #F0FDF4;
          color: #15803D;
          border: 1px solid #BBF7D0;
        }
      }
    }

    .rm-th-status, .rm-td-status {
      width: 82px;
      min-width: 76px;
      max-width: 88px;

      .rm-status-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 11.5px;
        font-weight: 700;
        white-space: nowrap;

        .rm-status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          margin-inline-end: 5px;
          flex-shrink: 0;
        }

        &.rm-badge-active {
          background: #ECFDF5;
          color: #059669;
          border: 1px solid #A7F3D0;

          .rm-status-dot {
            background: #10B981;
          }
        }

        &.rm-badge-inactive {
          background: #FEF3C7;
          color: #D97706;
          border: 1px solid #FDE68A;

          .rm-status-dot {
            background: #F59E0B;
          }
        }
      }
    }

    .rm-th-actions, .rm-td-actions {
      width: 135px;
      min-width: 130px;
      max-width: 142px;

      .rm-actions-group {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        white-space: nowrap;

        .rm-action-btn {
          width: 28px;
          height: 28px;
          min-width: 28px;
          border-radius: 6px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #E2E8F0;
          background: #FFFFFF;
          cursor: pointer;
          transition: all 0.15s ease;
          outline: none;
          padding: 0;

          &.rm-btn-view {
            color: #082C69;

            &:hover {
              background: rgba(8, 44, 105, 0.08);
              border-color: #082C69;
            }
          }

          &.rm-btn-edit {
            color: #D97706;

            &:hover {
              background: #FEF3C7;
              border-color: #F59E0B;
            }
          }

          &.rm-btn-deactivate {
            color: #B45309;

            &:hover {
              background: #FEF3C7;
              border-color: #F59E0B;
            }
          }

          &.rm-btn-activate {
            color: #059669;

            &:hover {
              background: #ECFDF5;
              border-color: #10B981;
            }
          }

          &.rm-btn-delete {
            color: #DC2626;

            &:hover {
              background: #FEE2E2;
              border-color: #EF4444;
            }
          }
        }
      }
    }
  }
}

  /* ==================== PAGINATION BAR ==================== */
  .rm-pagination-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
    padding: 16px 20px;
    background: #FFFFFF;
    border-top: 1px solid #E2E8F0;

    .rm-pagination-info {
      font-size: 13px;
      color: #64748B;
    }

    .rm-pagination-controls {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 12px;

      .rm-page-size-wrap {
        display: flex;
        align-items: center;

        .rm-page-size-label {
          font-size: 12.5px;
          color: #64748B;
          font-weight: 500;
        }

        .rm-page-size-select {
          width: 78px;

          ::v-deep .v-input__slot {
            min-height: 32px !important;
            font-size: 12.5px !important;
            border-radius: 6px !important;
            padding: 0 8px !important;
          }
        }
      }

      .rm-nav-buttons {
        display: flex;
        align-items: center;
        gap: 4px;

        .rm-nav-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: 1px solid #E2E8F0;
          background: #FFFFFF;
          color: #475569;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s ease;

          &:hover:not(:disabled) {
            background: #F1F5F9;
            border-color: #CBD5E1;
            color: #082C69;
          }

          &:disabled {
            opacity: 0.4;
            cursor: not-allowed;
          }
        }

        .rm-page-num {
          min-width: 32px;
          height: 32px;
          padding: 0 6px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 600;
          color: #475569;
          cursor: pointer;
          transition: all 0.15s ease;

          &:hover:not(.dots-page):not(.active-page) {
            background: #F1F5F9;
            color: #082C69;
          }

          &.active-page {
            background: #082C69;
            color: #FFFFFF;
            cursor: default;
          }

          &.dots-page {
            cursor: default;
            color: #94A3B8;
          }
        }
      }
    }
  }

/* ==================== RESPONSIVE BEHAVIOR ==================== */
@media (max-width: 768px) {
  .rm-header-row {
    flex-direction: column;
    align-items: stretch;

    .rm-header-actions {
      justify-content: stretch;

      .rm-btn-primary {
        width: 100%;
      }
    }
  }

  .rm-filter-row {
    flex-direction: column;
    align-items: stretch;

    .rm-search-box,
    .rm-filter-select-wrap {
      width: 100%;
      max-width: 100%;
    }
  }

  .rm-pagination-bar {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
}
</style>
